import { get, groupBy, has, isNull, isUndefined, size, sortBy, isArray, isEmpty } from 'lodash';
import moment from 'moment';
import {
  getTelemetryGraphDateTimeFormat,
  TELEMETRY_ACCURACY_DEFAULT_VALUE,
  TELEMETRY_MULTIPLICATOR_DEFAULT_VALUE,
  TELEMETRY_TIME_FORMAT,
} from '../../../../common/constants/telemetry';
import {
  AGGREGATION_INTERVALS,
  CONSOLIDATED_ECHARTS_HEIGHT,
  CONSOLIDATED_ECHARTS_INITIAL_INSIDE_TOP_GAP,
  GAP_BETWEEN_CONSOLIDATED_ECHARTS,
} from './const';

// Use X for seconds format and x for milliseconds depends on API implementation
export const toUTCFormat = (value, offset) => moment(value, TELEMETRY_TIME_FORMAT).utcOffset(offset);

export const prepareFilteredMetricsToAPI = (metrics, aggregation) =>
  metrics.reduce((acc, metric) => {
    acc[metric] = { type: aggregation };

    return acc;
  }, {});

export const prepareFiltersToAPI = (filters) => {
  const tsFrom = +filters.from;
  const tsTo = +filters.to;
  const interval = filters.TAS.getSuitableInterval({
    start: tsFrom,
    end: tsTo,
    linesCount: 3,
  });

  return {
    tsFrom,
    tsTo,
    interval,
    metrics: prepareFilteredMetricsToAPI(
      filters.metrics.filter((metric) => metric !== ''),
      filters.aggregation,
    ),
    chargerUuids: filters.chargerUuids,
  };
};

export const convertMetricListToObject = (list) =>
  list.reduce((acc, metricValue) => {
    const metric = Object.keys(metricValue)[0];

    acc[metric] = metricValue[metric];

    return acc;
  }, {});

export const prepareChargerMetricsToStore = (chargerId, payload) =>
  get(payload, chargerId, []).reduce((acc, data) => {
    const { ts, values } = data;

    acc[ts] = convertMetricListToObject(values);

    return acc;
  }, {});

export const detectSuitableAggregationInterval = ({ start, end, resultCount }) => {
  const diff = end - start;

  return Object.keys(AGGREGATION_INTERVALS).find((interval) => {
    const { milliseconds } = AGGREGATION_INTERVALS[interval];
    const slots = Math.floor(diff / milliseconds);

    return slots <= resultCount;
  });
};

export const prepareChargerSnapshotToStore = (details, session) => ({
  details: {
    onlineStatus: get(details, 'i2p2_state.c_online') ? 'online' : 'offline',
    cableStatus: ['CONNECTED_CHARGING', 'CONNECTED_NOT_CHARGING'].includes(get(details, 'charger_state')) ? 'connected' : 'disconnected',
  },
  session:
    session && !session.end_time
      ? {
          id: session.session_id,
          type: session.session_type,
          duration: session.duration,
          userIdTag: session.id_tag,
          start_time: session.start_time,
        }
      : null,
});

//= ==================================
const convertToOffsetTime = (ts, offset) => moment(ts, TELEMETRY_TIME_FORMAT).utcOffset(offset);

const dateTimeFormatter = (ts, isHistory, offset, isSameDay = true) => {
  if (isHistory) {
    const value = convertToOffsetTime(ts, offset);
    const format = getTelemetryGraphDateTimeFormat({ showDate: !isSameDay, dateInNewLine: true });

    return value.format(format);
  }
  const format = getTelemetryGraphDateTimeFormat({ showDate: !isSameDay, dateInNewLine: true });

  return convertToOffsetTime(ts, offset).format(format);
};

export const convertMetricValue = (value, metric) => {
  if (isNull(value)) {
    return null;
  }

  const { multiplicator = TELEMETRY_MULTIPLICATOR_DEFAULT_VALUE, accuracy = TELEMETRY_ACCURACY_DEFAULT_VALUE } = metric;

  return (value * multiplicator).toFixed(accuracy);
};

const getValue = (item, metric) => {
  const value = get(item, 'value.1', null);

  return isNull(value) ? 'N/A' : `${value} ${metric.units}`;
};

const getTooltipLineItems = (items, metric) =>
  size(items) <= 0
    ? ''
    : items
        .map(
          (item) => `
        <div style="display: inline-block; float: left">${item.marker}</div> 
        <div style="display: inline-block; float: left; padding-right: 10px;">
          ${item.seriesName || metric.title}: ${getValue(item, metric)}</div>
        `,
        )
        .join('</br>');

const sortSeriesData = (prev, next) => parseInt(prev, 10) - parseInt(next, 10);

const getAggregationColor = (agg) => {
  switch (agg) {
    case 'avg':
      return { color: '#0059ff' };
    case 'max':
      return { color: '#ff0000' };
    case 'min':
    default:
      return { color: '#27be00' };
  }
};

const defaultSeriesParams = {
  type: 'line',
  showSymbol: true,
  symbol: 'circle',
  symbolSize: 4,
  animation: false,
  emphasis: {
    scale: false,
  },
};

const createSeries = ({ chargerMetrics, dataKeys, metric, withAggregation }) => {
  const seriesParams = defaultSeriesParams;

  if (withAggregation) {
    return dataKeys.map((aggregation) => ({
      ...seriesParams,
      name: aggregation.toUpperCase(),
      lineStyle: getAggregationColor(aggregation),
      itemStyle: getAggregationColor(aggregation),
      data: Object.keys(chargerMetrics[aggregation])
        .sort(sortSeriesData)
        .map((ts) => [parseInt(ts, 10), convertMetricValue(chargerMetrics[aggregation][ts][metric.code], metric)]),
    }));
  }

  return [
    {
      ...seriesParams,
      data: dataKeys.sort(sortSeriesData).map((ts) => [parseInt(ts, 10), convertMetricValue(chargerMetrics[ts][metric.code], metric)]),
    },
  ];
};

const createRTSeries = ({ chargerMetrics, metric }) => {
  const series = [];
  const seriesWithSessions = defaultSeriesParams;
  const seriesWithoutSessions = {
    ...defaultSeriesParams,
    showSymbol: true,
    symbol: 'triangle',
    symbolSize: 10,
    lineStyle: { width: 0 },
  };

  const sessions = groupBy(chargerMetrics[metric.code], 'sessionId');

  for (const sessionId in sessions) {
    const data = sortBy(sessions[sessionId], (item) => item.ts).map((item) => [
      item.ts,
      convertMetricValue(item[metric.code], metric),
      isUndefined(sessionId) || sessionId === 'undefined' ? 'N/A' : sessionId,
    ]);
    const params = isUndefined(sessionId) || sessionId === 'undefined' ? seriesWithoutSessions : seriesWithSessions;

    series.push({
      ...params,
      sessionId,
      data,
    });
  }

  return series;
};

const generateSeriesItem = ({ seriesName, marker, value }, units) => {
  const [, data, sessionId] = value;
  const valueToShow = isNull(data) || data === 'NaN' ? 'N/A' : `${data} ${units}`;
  const chartName = seriesName.startsWith('series') ? valueToShow : `${seriesName}: ${valueToShow}`;
  const sessionInfo = sessionId ? `<div>Session ID: ${sessionId}</div>` : '';

  return `
    <div>
      ${marker}
      <span>${chartName}</span>
      ${sessionInfo}
    </div>
  `;
};

const generateGraphBlock = ({ values, graphName, units }) => {
  const seriesItems = values.map((item) => generateSeriesItem(item, units, graphName)).join('');
  const seriesRow = seriesItems || 'No data found';

  return `
    <div>
      <strong>${graphName}</strong>
      <div>${seriesRow}</div>
    </div>
  `;
};

const getConsolidatedTooltipLineItems = (groupOptions) => `
    <div style="display: flex; flex-wrap: wrap; gap: 20px">
      ${groupOptions.map(generateGraphBlock).join('')}
    </div>
  `;

export const buildConsolidatedGraphOption = ({
  selectedMetrics,
  chargerMetrics,
  start,
  end,
  offset,
  isDemo,
  isHistory = false,
  withAggregation = false,
}) => {
  const dataKeys = Object.keys(chargerMetrics);
  const isSameDay = moment(start).startOf('day').isSame(moment(end).startOf('day'));
  const metricIndexes = selectedMetrics.map((_, index) => index);

  const series = metricIndexes.flatMap((index) =>
    (isHistory || isDemo
      ? createSeries({ chargerMetrics, dataKeys, metric: selectedMetrics[index], withAggregation })
      : createRTSeries({ chargerMetrics, metric: selectedMetrics[index] })
    ).map((option) => ({
      ...option,
      xAxisIndex: index,
      yAxisIndex: index,
    })),
  );

  const xAxis = metricIndexes.map((index) => ({
    type: 'time',
    min: +start,
    max: +end,
    axisLabel: { formatter: (ts) => dateTimeFormatter(ts, isHistory, offset, isSameDay) },
    boundaryGap: false,
    silent: true,
    gridIndex: index,
  }));

  const yAxis = selectedMetrics.map(({ title, units }, index) => ({
    type: 'value',
    scale: true,
    name: `${title} (${units})`,
    gridIndex: index,
    units,
    nameTextStyle: {
      fontSize: 0,
    },
  }));

  const grid = metricIndexes.map((index) => ({
    top: GAP_BETWEEN_CONSOLIDATED_ECHARTS * index + CONSOLIDATED_ECHARTS_INITIAL_INSIDE_TOP_GAP,
    height: CONSOLIDATED_ECHARTS_HEIGHT,
    left: 90,
    right: 40,
  }));

  const dataZoom = [
    {
      xAxisIndex: metricIndexes,
      type: 'slider',
      show: false,
    },
    {
      xAxisIndex: metricIndexes,
      type: 'inside',
      show: false,
    },
  ];

  const title = selectedMetrics.map(({ title, units }, index) => ({
    textStyle: {
      fontSize: 16,
    },
    text: `${title} (${units})`,
    top: grid[index].top - 40,
    left: 20,
  }));

  const tooltipFormatter = (params) => {
    const groupOptions = yAxis.map(({ gridIndex: axisIndex, name: graphName, units }) => ({
      graphName,
      units,
      values: params.filter(({ axisIndex: idx }) => idx === axisIndex),
    }));

    const chargersTooltipItems = getConsolidatedTooltipLineItems(groupOptions);

    return `
        <div>
          ${convertToOffsetTime(params[0].value[0], offset).format('MMM, DD YYYY LTS')}
        </div>
        ${chargersTooltipItems}
    `;
  };

  return {
    xAxis,
    yAxis,
    grid,
    title,
    dataZoom,
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter,
      confine: true,
    },
    axisPointer: {
      link: {
        xAxisIndex: 'all',
      },
    },
    series,
  };
};

export const buildGraphOption = (props) => {
  const { metric, dataKeys, chargerMetrics, start, end, offset, isHistory = false, withAggregation = false, isDemo } = props;

  const series =
    isHistory || isDemo
      ? createSeries({
          chargerMetrics,
          dataKeys,
          metric,
          withAggregation,
        })
      : createRTSeries({ chargerMetrics, metric });

  const tooltipFormatter = (params) => {
    if (!withAggregation) {
      const { data } = params[0];
      const [dateValue, metricValue, sessionId] = data;
      const date = convertToOffsetTime(dateValue, offset).format('MMM, DD YYYY LTS');
      const value = Number(metricValue).toFixed(metric.accuracy);
      const valueToShow = isNull(value) ? 'N/A' : `${value} ${metric.units}`;

      const session = sessionId ? `Session ID: ${sessionId}` : null;

      if (session) {
        return `${date}<br/>${metric.title}: ${valueToShow}<br/>${session}`;
      }

      return `${date}<br/>${metric.title}: ${valueToShow}`;
    }
    const chargersTooltipItems = getTooltipLineItems(params, metric);

    return `<div style="display: flex; justify-content: center;">
          ${convertToOffsetTime(params[0].value[0], offset).format('MMM, DD YYYY LTS')}</div>
            ${chargersTooltipItems}`;
  };

  const isSameDay = moment(start).startOf('day').isSame(moment(end).startOf('day'));

  return {
    grid: {
      left: 80,
      right: 80,
      top: 40,
      bottom: 80,
    },
    tooltip: {
      trigger: 'axis',
      renderMode: 'html',
      confine: true,
      appendToBody: true,
      formatter: tooltipFormatter,
    },
    xAxis: {
      type: 'time',
      min: +start,
      max: +end,
      scale: 'value',
      axisLabel: { formatter: (ts) => dateTimeFormatter(ts, isHistory, offset, isSameDay) },
      boundaryGap: false,
      silent: true,
    },
    yAxis: {
      type: 'value',
      scale: 'value',
    },
    dataZoom: [
      {
        type: 'slider',
        showDataShadow: false,
        filterMode: 'none',
        labelFormatter: (ts) => dateTimeFormatter(ts, isHistory, offset),
      },
      { type: 'inside' },
    ],
    series,
  };
};

export const getHistoryExportData = (csvData, chargerMetrics, metrics, offset) => {
  const metricList = isArray(metrics) ? metrics : [metrics];

  return [
    ...csvData,
    ...metricList.flatMap((metric) =>
      ['min', 'max', 'avg'].flatMap((agg) => {
        const aggData = chargerMetrics[agg];

        return Object.entries(aggData)
          .filter(([, data]) => has(data, metric.code) && !isNull(data[metric.code]))
          .map(([ts, data]) => ({
            ts: convertToOffsetTime(ts, offset).format('YYYY-MM-DD HH:mm:ss'),
            agg,
            value: convertMetricValue(data[metric.code], metric),
            metricTitle: metric.title,
          }));
      }),
    ),
  ];
};

export const getRealTimeExportData = (csvData, metricKeys, chargerMetrics, metrics, offset) => {
  const metricList = Array.isArray(metrics) ? metrics : [metrics];

  if (!chargerMetrics || isEmpty(chargerMetrics)) return null;

  return [
    ...csvData,
    ...metricList.flatMap((metric) => {
      if (!chargerMetrics[metric.code]) return [];

      const dates = Object.keys(chargerMetrics[metric.code]).map(Number);

      return dates.map((date) => {
        const time = convertToOffsetTime(date, offset).format('YYYY-MM-DD HH:mm:ss');
        const valueOption = get(chargerMetrics, `${metric.code}.${date}.${metric.code}`, null);

        return !isNull(valueOption) ? { ts: time, value: convertMetricValue(valueOption, metric), metricTitle: metric.title } : null;
      });
    }),
  ];
};

export const getRealTimeExportDemoData = (csvData, dates, chargerMetrics, metrics, offset) => {
  const metricList = isArray(metrics) ? metrics : [metrics];

  return [
    ...csvData,
    ...metricList.flatMap((metric) =>
      dates
        .filter(
          (date) =>
            Object.prototype.hasOwnProperty.call(chargerMetrics, date) &&
            Object.prototype.hasOwnProperty.call(chargerMetrics[date], metric.code),
        )
        .map((date) => {
          const time = convertToOffsetTime(date, offset).format('YYYY-MM-DD HH:mm:ss');

          return chargerMetrics[date][metric.code] !== null
            ? { ts: time, value: convertMetricValue(chargerMetrics[date][metric.code], metric), metricTitle: metric.title }
            : null;
        }),
    ),
  ];
};
